import { useCallback, useRef } from 'react';

import type { WregIntakeForm } from '@/components/webreg/screens/wregIntakeFields';
import { buildLeadSnapshot } from '@/components/webreg/wregCampaignsLead';
import { WREG_STEP_NAME, type WregStep } from '@/components/webreg/wregSteps';
import { API_CALL_RESPONSE, API_SERVICES, type ApiService } from '@/consts/events';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRecaptchaMint } from '@/hooks/useRecaptchaMint';
import {
  campaignsEnabled,
  linkSession,
  postLead,
  type CampaignsOutcome,
} from '@/services/campaignsClient';

export const CAMPAIGNS_LEAD_ACTION = 'wreg_lead';

// A mint that never settles would hold the queue, and every later request with it. The server
// accepts a lead without the header.
const MINT_TIMEOUT_MS = 5000;

const withTimeout = (pending: Promise<string | undefined>): Promise<string | undefined> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), MINT_TIMEOUT_MS);
    pending.then(resolve, () => resolve(undefined)).finally(() => clearTimeout(timer));
  });

const retryable = (status: number | undefined): boolean =>
  status === undefined || status === 429 || status >= 500;

export interface WregCampaigns {
  sendLead: (form: WregIntakeForm, step: WregStep) => void;
  link: (authToken: string) => void;
}

// Fire and forget: callers never await these and neither throws. Everything runs one request at a
// time on a single queue, because the session cookie is created by the first response (two in
// flight could make two sessions and two leads), and because a lead is frozen once linked, so the
// link has to wait for any snapshot still in flight.
export const useWregCampaigns = (gateOn: boolean): WregCampaigns => {
  const { webregUserId, utmParams, registrationData } = useRegistration();
  const promoCode = registrationData.promoCodeData?.code;
  const mintToken = useRecaptchaMint(CAMPAIGNS_LEAD_ACTION);
  const { trackError } = useAnalytics();
  const latest = useRef({ gateOn, mintToken, trackError });
  latest.current = { gateOn, mintToken, trackError };
  const tail = useRef<Promise<void>>(Promise.resolve());
  const linkedFor = useRef<string | null>(null);

  // Failure rows only, and nothing from the request: the status or `network`, never a body.
  const enqueue = useCallback(
    (
      service: ApiService,
      step: WregStep,
      task: () => Promise<CampaignsOutcome>,
      onRetryable?: () => void,
    ): void => {
      tail.current = tail.current.then(async () => {
        try {
          const outcome = await task();
          if (outcome.ok) return;
          if (retryable(outcome.status)) onRetryable?.();
          latest.current.trackError({
            event: API_CALL_RESPONSE,
            properties: {
              service,
              status: outcome.status ?? 'network',
              step_name: WREG_STEP_NAME[step],
            },
          });
        } catch {
          // A rejection would break the queue for every later request, and the report must not either.
          try {
            latest.current.trackError({
              event: API_CALL_RESPONSE,
              properties: { service, status: 'exception', step_name: WREG_STEP_NAME[step] },
            });
          } catch {
            // Nothing left to do.
          }
        }
      });
    },
    [],
  );

  const sendLead = useCallback(
    (form: WregIntakeForm, step: WregStep): void => {
      if (!latest.current.gateOn || !campaignsEnabled()) return;
      try {
        const body = buildLeadSnapshot(form, { webregUserId, utmParams, promoCode });
        if (Object.keys(body).length === 0) return;
        enqueue(API_SERVICES.CAMPAIGNS_LEAD, step, async () => {
          // Minted here, not at call time: a token is single use and short lived, and this
          // request may have waited behind another.
          const token = await withTimeout(latest.current.mintToken(CAMPAIGNS_LEAD_ACTION));
          return postLead(body, token);
        });
      } catch {
        // Runs before navigation, so a throw here would strand the member on the screen.
      }
    },
    [enqueue, promoCode, utmParams, webregUserId],
  );

  // Once per account: a shipping retry passes the signup point again with the same token.
  const link = useCallback(
    (authToken: string): void => {
      if (!latest.current.gateOn || !campaignsEnabled() || linkedFor.current === authToken) return;
      linkedFor.current = authToken;
      enqueue(
        API_SERVICES.CAMPAIGNS_LINK,
        'monitor',
        () => linkSession(authToken),
        () => {
          linkedFor.current = null;
        },
      );
    },
    [enqueue],
  );

  return { sendLead, link };
};
